import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@/app/hooks';
import { getClientId } from '@/utils/clientId';
import { useUnlockMutation } from './waiterApi';
import NameTakeoverPrompt, { ActiveDevice } from './NameTakeoverPrompt';
import { setUiLocked, setWaiterAuth } from './waiterSlice';

function lastUsedKey(restaurantUuid: string): string {
  return `waiter:lastName:${restaurantUuid}`;
}

/** SC-576 — 409 WAITER_NAME_IN_USE: the picked name is live on another device. */
interface PickErrorShape {
  status?: number | string;
  data?: {
    code?: string;
    waiter_name?: string;
    active_devices?: ActiveDevice[];
  };
}

interface NameConflict {
  name: string;
  devices: ActiveDevice[];
}

interface NamePickerProps {
  restaurantUuid: string;
  waiterNames: string[];
  /** The PIN validated moments ago by PinPad — needed to persist the picked
   * name server-side (spec §2.3: name switch = re-PIN). Absent after e.g. a
   * page reload; in that case we send the waiter back to the PIN pad. */
  pin: string | null;
}

export default function NamePicker({ restaurantUuid, waiterNames, pin }: NamePickerProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [unlock, { isLoading }] = useUnlockMutation();
  const [pending, setPending] = useState<string | null>(null);
  const [errorShown, setErrorShown] = useState(false);
  const [conflict, setConflict] = useState<NameConflict | null>(null);
  const [lastUsed] = useState<string | null>(() => {
    try {
      return localStorage.getItem(lastUsedKey(restaurantUuid));
    } catch {
      return null;
    }
  });

  if (!pin) {
    return (
      <div className="waiter-surface min-h-[100dvh] flex flex-col items-center justify-center gap-4 p-6 bg-cream">
        <p className="font-display text-lg text-ink">{t('waiter.namePicker.reconnect')}</p>
        <button
          type="button"
          onClick={() => dispatch(setUiLocked(true))}
          className="bg-ink text-paper font-display px-4 py-2 rounded-btn-sm"
        >
          {t('waiter.pinPad.title')}
        </button>
      </div>
    );
  }

  const handlePick = async (name: string, forceNameTakeover = false) => {
    setErrorShown(false);
    setPending(name);
    try {
      const result = await unlock({
        restaurantUuid,
        pin,
        deviceId: getClientId(),
        waiterName: name,
        forceNameTakeover,
      }).unwrap();
      setConflict(null);
      dispatch(
        setWaiterAuth({
          restaurantUuid,
          token: result.token,
          deviceUuid: result.deviceUuid,
          waiterName: name,
          waiterNames: result.waiterNames,
          idleLockSeconds: result.idleLockSeconds,
          expiresAt: result.expiresAt,
        }),
      );
      try {
        localStorage.setItem(lastUsedKey(restaurantUuid), name);
      } catch {
        // localStorage unavailable — preselect-last-used is a nicety, not
        // required for the flow to work.
      }
    } catch (err: unknown) {
      const apiErr = err as PickErrorShape;
      // SC-576: this name is already serving on another device. Confirming
      // signs that device out, so ask before doing it — and keep the prompt up
      // if the confirmed retry itself fails.
      if (apiErr.status === 409 && apiErr.data?.code === 'WAITER_NAME_IN_USE') {
        setConflict({
          name: apiErr.data.waiter_name || name,
          devices: apiErr.data.active_devices ?? [],
        });
        return;
      }
      setErrorShown(true);
    } finally {
      setPending(null);
    }
  };

  if (conflict) {
    return (
      <NameTakeoverPrompt
        waiterName={conflict.name}
        devices={conflict.devices}
        busy={isLoading}
        error={errorShown ? t('waiter.nameTakeover.error') : null}
        onConfirm={() => void handlePick(conflict.name, true)}
        onCancel={() => {
          setConflict(null);
          setErrorShown(false);
        }}
      />
    );
  }

  return (
    <div className="waiter-surface min-h-[100dvh] flex flex-col items-center justify-center gap-6 p-6 bg-cream">
      <h1 className="font-display text-2xl text-ink">{t('waiter.namePicker.title')}</h1>
      {errorShown && (
        <p className="text-sm text-red-600" role="alert">
          {t('waiter.namePicker.error')}
        </p>
      )}
      <div className="flex flex-wrap gap-3 justify-center max-w-md">
        {waiterNames.map((name) => (
          <button
            key={name}
            type="button"
            onClick={() => void handlePick(name)}
            disabled={isLoading}
            className={`bg-paper border-2 border-ink rounded-btn px-5 py-3 font-display text-lg disabled:opacity-50 ${
              name === lastUsed ? 'ring-2 ring-mint' : ''
            }`}
          >
            {pending === name ? t('waiter.namePicker.saving') : name}
          </button>
        ))}
      </div>
    </div>
  );
}
